import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { tanksApi, customersApi } from '../../services/api';
import { format, differenceInDays, parseISO, addYears } from 'date-fns';
import { it } from 'date-fns/locale';
import ResponsiveHeader from '../../components/ResponsiveHeader';
import ResponsiveFilters from '../../components/ResponsiveFilters';
import MobileCardList from '../../components/MobileCardList';

const TankList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTank, setEditingTank] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tankToDelete, setTankToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    matricola: '',
    capacity: '',
    installation_date: '',
    last_inspection: '',
    next_inspection: '',
    status: 'attivo',
    location: '',
    notes: '',
    customer_id: null,
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Query serbatoi
  const { data: tanksData, isLoading, refetch } = useQuery({
    queryKey: ['tanks'],
    queryFn: () => tanksApi.getAll(),
  });

  // Query clienti per autocomplete
  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: () => customersApi.getAll({ limit: 1000 }),
  });

  // Query serbatoi in scadenza
  const { data: expiringData } = useQuery({
    queryKey: ['tanks-expiring'],
    queryFn: () => tanksApi.getExpiring(90),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: tanksApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['tanks']);
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => tanksApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['tanks']);
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tanksApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['tanks']);
      setDeleteDialogOpen(false);
      setTankToDelete(null);
    },
  });

  const tanks = tanksData?.data?.data || tanksData?.data || [];
  const customers = customersData?.data?.data || customersData?.data || [];
  const expiringCount = expiringData?.data?.data?.length || 0;

  // Calcola stato scadenza
  const getExpiryStatus = (nextInspection) => {
    if (!nextInspection) return 'unknown';
    const days = differenceInDays(parseISO(nextInspection), new Date());
    if (days < 0) return 'expired';
    if (days <= 30) return 'critical';
    if (days <= 90) return 'warning';
    return 'ok';
  };

  // Filtra serbatoi
  const filteredTanks = useMemo(() => {
    return tanks.filter((tank) => {
      // Filtro ricerca
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        tank.matricola?.toLowerCase().includes(searchLower) ||
        tank.Customer?.company_name?.toLowerCase().includes(searchLower) ||
        tank.location?.toLowerCase().includes(searchLower);

      // Filtro stato
      const matchesStatus =
        statusFilter === 'all' || tank.status === statusFilter;

      // Filtro scadenza
      const expiryStatus = getExpiryStatus(tank.next_inspection);
      const matchesExpiry =
        expiryFilter === 'all' ||
        (expiryFilter === 'expired' && expiryStatus === 'expired') ||
        (expiryFilter === 'expiring' && ['critical', 'warning'].includes(expiryStatus)) ||
        (expiryFilter === 'ok' && expiryStatus === 'ok');

      return matchesSearch && matchesStatus && matchesExpiry;
    });
  }, [tanks, search, statusFilter, expiryFilter]);

  const handleOpenDialog = (tank = null) => {
    if (tank) {
      setEditingTank(tank);
      setFormData({
        matricola: tank.matricola || '',
        capacity: tank.capacity || '',
        installation_date: tank.installation_date?.split('T')[0] || '',
        last_inspection: tank.last_inspection?.split('T')[0] || '',
        next_inspection: tank.next_inspection?.split('T')[0] || '',
        status: tank.status || 'attivo',
        location: tank.location || '',
        notes: tank.notes || '',
        customer_id: tank.customer_id,
      });
      setSelectedCustomer(tank.Customer || null);
    } else {
      setEditingTank(null);
      setFormData({
        matricola: '',
        capacity: '',
        installation_date: '',
        last_inspection: '',
        next_inspection: '',
        status: 'attivo',
        location: '',
        notes: '',
        customer_id: null,
      });
      setSelectedCustomer(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTank(null);
  };

  const handleSubmit = () => {
    const data = {
      ...formData,
      customer_id: selectedCustomer?.id || null,
      capacity: formData.capacity ? parseFloat(formData.capacity) : null,
    };

    if (editingTank) {
      updateMutation.mutate({ id: editingTank.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleLastInspectionChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      last_inspection: date,
      // Calcola automaticamente prossima revisione (+10 anni)
      next_inspection: date ? format(addYears(parseISO(date), 10), 'yyyy-MM-dd') : '',
    }));
  };

  const columns = [
    {
      field: 'matricola',
      headerName: 'Matricola',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'customer',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.Customer?.company_name || '-',
    },
    {
      field: 'capacity',
      headerName: 'Capacità (L)',
      width: 120,
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString('it-IT') : '-',
    },
    {
      field: 'location',
      headerName: 'Ubicazione',
      width: 180,
    },
    {
      field: 'installation_date',
      headerName: 'Installazione',
      width: 120,
      valueFormatter: (params) =>
        params.value
          ? format(parseISO(params.value), 'dd/MM/yyyy', { locale: it })
          : '-',
    },
    {
      field: 'next_inspection',
      headerName: 'Prossima Revisione',
      width: 160,
      renderCell: (params) => {
        const status = getExpiryStatus(params.value);
        const colors = {
          expired: 'error',
          critical: 'error',
          warning: 'warning',
          ok: 'success',
          unknown: 'default',
        };
        const icons = {
          expired: <ErrorIcon fontSize="small" />,
          critical: <WarningIcon fontSize="small" />,
          warning: <WarningIcon fontSize="small" />,
          ok: <CheckIcon fontSize="small" />,
          unknown: null,
        };
        const labels = {
          expired: 'Scaduta',
          critical: 'Urgente',
          warning: 'In scadenza',
          ok: 'OK',
          unknown: 'N/D',
        };

        return (
          <Tooltip title={labels[status]}>
            <Chip
              icon={icons[status]}
              label={
                params.value
                  ? format(parseISO(params.value), 'dd/MM/yyyy', { locale: it })
                  : 'N/D'
              }
              color={colors[status]}
              size="small"
              variant={status === 'ok' ? 'outlined' : 'filled'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Stato',
      width: 120,
      renderCell: (params) => {
        const statusColors = {
          attivo: 'success',
          inattivo: 'default',
          manutenzione: 'warning',
          dismesso: 'error',
        };
        const statusLabels = {
          attivo: 'Attivo',
          inattivo: 'Inattivo',
          manutenzione: 'Manutenzione',
          dismesso: 'Dismesso',
        };
        return (
          <Chip
            label={statusLabels[params.value] || params.value}
            color={statusColors[params.value] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setTankToDelete(params.row);
              setDeleteDialogOpen(true);
            }}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      {/* Header */}
      <ResponsiveHeader
        title="Serbatoi"
        subtitle="Gestione parco serbatoi GPL"
        primaryAction={{
          label: isMobile ? 'Nuovo' : 'Nuovo Serbatoio',
          icon: <AddIcon />,
          onClick: () => handleOpenDialog(),
        }}
        secondaryActions={[
          { label: 'Aggiorna', icon: <RefreshIcon />, onClick: () => refetch() },
        ]}
      />

      {/* Alert serbatoi in scadenza */}
      {expiringCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{expiringCount} serbatoi</strong> con revisione in scadenza nei
          prossimi 90 giorni
        </Alert>
      )}

      {/* Filtri */}
      <Card sx={{ mb: isMobile ? 1 : 2 }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <ResponsiveFilters
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cerca matricola, cliente..."
            activeFilterCount={(statusFilter !== 'all' ? 1 : 0) + (expiryFilter !== 'all' ? 1 : 0)}
            onClearFilters={() => { setStatusFilter('all'); setExpiryFilter('all'); }}
          >
            <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }} size="small">
              <InputLabel>Stato</InputLabel>
              <Select
                value={statusFilter}
                label="Stato"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="attivo">Attivo</MenuItem>
                <MenuItem value="inattivo">Inattivo</MenuItem>
                <MenuItem value="manutenzione">Manutenzione</MenuItem>
                <MenuItem value="dismesso">Dismesso</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 170 } }} size="small">
              <InputLabel>Scadenza Revisione</InputLabel>
              <Select
                value={expiryFilter}
                label="Scadenza Revisione"
                onChange={(e) => setExpiryFilter(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="expired">Scaduti</MenuItem>
                <MenuItem value="expiring">In scadenza</MenuItem>
                <MenuItem value="ok">In regola</MenuItem>
              </Select>
            </FormControl>
          </ResponsiveFilters>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {filteredTanks.length} serbatoi
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Lista/DataGrid */}
      {isMobile ? (
        <MobileCardList
          rows={filteredTanks}
          loading={isLoading}
          emptyMessage="Nessun serbatoio trovato"
          renderCard={(tank) => {
            const expiryStatus = getExpiryStatus(tank.next_inspection);
            const expiryColors = { expired: 'error', critical: 'error', warning: 'warning', ok: 'success', unknown: 'default' };
            const expiryLabels = { expired: 'Scaduta', critical: 'Urgente', warning: 'In scadenza', ok: 'OK', unknown: 'N/D' };
            const statusColors = { attivo: 'success', inattivo: 'default', manutenzione: 'warning', dismesso: 'error' };
            const statusLabels = { attivo: 'Attivo', inattivo: 'Inattivo', manutenzione: 'Manutenzione', dismesso: 'Dismesso' };
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="medium">{tank.matricola || '-'}</Typography>
                    <Chip
                      label={statusLabels[tank.status] || tank.status}
                      color={statusColors[tank.status] || 'default'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Typography variant="body2" noWrap color="text.secondary" sx={{ mb: 0.3 }}>
                    {tank.Customer?.company_name || '-'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {tank.capacity && <Typography variant="caption" color="text.secondary">{tank.capacity.toLocaleString('it-IT')} L</Typography>}
                    {tank.location && <Typography variant="caption" color="text.secondary">{tank.location}</Typography>}
                    <Chip
                      label={tank.next_inspection ? format(parseISO(tank.next_inspection), 'dd/MM/yy') : 'N/D'}
                      color={expiryColors[expiryStatus]}
                      size="small"
                      variant={expiryStatus === 'ok' ? 'outlined' : 'filled'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 0.5 }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDialog(tank); }} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setTankToDelete(tank); setDeleteDialogOpen(true); }} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          }}
        />
      ) : (
        <Card>
          <DataGrid
            rows={filteredTanks}
            columns={columns}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'next_inspection', sort: 'asc' }] },
            }}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
              },
            }}
          />
        </Card>
      )}

      {/* Dialog Creazione/Modifica */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingTank ? 'Modifica Serbatoio' : 'Nuovo Serbatoio'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Matricola"
                value={formData.matricola}
                onChange={(e) =>
                  setFormData({ ...formData, matricola: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capacità (Litri)"
                type="number"
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({ ...formData, capacity: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) =>
                  option.company_name || `${option.first_name} ${option.last_name}`
                }
                value={selectedCustomer}
                onChange={(_, newValue) => setSelectedCustomer(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente Assegnato" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data Installazione"
                type="date"
                value={formData.installation_date}
                onChange={(e) =>
                  setFormData({ ...formData, installation_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Stato</InputLabel>
                <Select
                  value={formData.status}
                  label="Stato"
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <MenuItem value="attivo">Attivo</MenuItem>
                  <MenuItem value="inattivo">Inattivo</MenuItem>
                  <MenuItem value="manutenzione">Manutenzione</MenuItem>
                  <MenuItem value="dismesso">Dismesso</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ultima Revisione"
                type="date"
                value={formData.last_inspection}
                onChange={(e) => handleLastInspectionChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="La prossima revisione viene calcolata automaticamente (+10 anni)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prossima Revisione"
                type="date"
                value={formData.next_inspection}
                onChange={(e) =>
                  setFormData({ ...formData, next_inspection: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ubicazione"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="Indirizzo installazione serbatoio"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !formData.matricola ||
              createMutation.isPending ||
              updateMutation.isPending
            }
          >
            {editingTank ? 'Salva Modifiche' : 'Crea Serbatoio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il serbatoio{' '}
            <strong>{tankToDelete?.matricola}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate(tankToDelete?.id)}
            disabled={deleteMutation.isPending}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TankList;
